import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { Check, Minus } from 'lucide-react';
import {
  activateAccount,
  describeError,
  lookupInvitation,
  remainingAttempts,
  type InvitationLookupResponse,
} from '@/lib/api';
import { AxiosError } from 'axios';

const CODE_LEN = 4;
const CODE_ALPHABET = /^[A-Z2-9]$/;

interface PasswordCriteria {
  length: boolean;
  upper: boolean;
  lower: boolean;
  digit: boolean;
  symbol: boolean;
}

function evaluatePassword(p: string): PasswordCriteria {
  return {
    length: p.length >= 12,
    upper: /[A-Z]/.test(p),
    lower: /[a-z]/.test(p),
    digit: /[0-9]/.test(p),
    symbol: /[^A-Za-z0-9]/.test(p),
  };
}

export function ActivationPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') ?? '';

  const [lookup, setLookup] = useState<InvitationLookupResponse | null>(null);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [lookupStatus, setLookupStatus] = useState<number | null>(null);
  const [codeChars, setCodeChars] = useState<string[]>(Array(CODE_LEN).fill(''));
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [remaining, setRemaining] = useState<number | null>(null);
  const inputsRef = useRef<Array<HTMLInputElement | null>>([]);

  // Sem token na URL o erro esta decidido antes do primeiro render — nao ha
  // o que buscar, e nao ha por que escrever isso num efeito.
  const lookupError = !token
    ? 'Link de ativação inválido — falta o token.'
    : fetchError;

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    lookupInvitation(token)
      .then((data) => {
        if (cancelled) return;
        setLookup(data);
      })
      .catch((err) => {
        if (cancelled) return;
        if (err instanceof AxiosError) {
          setLookupStatus(err.response?.status ?? 0);
        }
        setFetchError(describeError(err));
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  function handleCodeChange(idx: number, raw: string) {
    const upper = raw.toUpperCase();
    if (upper.length <= 1) {
      const next = [...codeChars];
      next[idx] = CODE_ALPHABET.test(upper) ? upper : '';
      setCodeChars(next);
      if (next[idx] && idx < CODE_LEN - 1) {
        inputsRef.current[idx + 1]?.focus();
      }
      return;
    }
    // Paste path: distribute chars across remaining inputs.
    const chars = upper.split('').filter((c) => CODE_ALPHABET.test(c));
    const next = [...codeChars];
    let cursor = idx;
    for (const c of chars) {
      if (cursor >= CODE_LEN) break;
      next[cursor] = c;
      cursor += 1;
    }
    setCodeChars(next);
    const focusIdx = Math.min(cursor, CODE_LEN - 1);
    inputsRef.current[focusIdx]?.focus();
  }

  function handleCodeKeyDown(idx: number, e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Backspace' && !codeChars[idx] && idx > 0) {
      e.preventDefault();
      inputsRef.current[idx - 1]?.focus();
    }
  }

  const criteria = evaluatePassword(password);
  const passwordOk = Object.values(criteria).every(Boolean);
  const codeOk = codeChars.every((c) => CODE_ALPHABET.test(c));

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!passwordOk || !codeOk) return;
    setSubmitError(null);
    setSubmitting(true);
    try {
      await activateAccount({
        token,
        verificationCode: codeChars.join(''),
        newPassword: password,
      });
      navigate('/login/mfa-enroll', { replace: true });
    } catch (err) {
      if (err instanceof AxiosError) {
        const status = err.response?.status ?? 0;
        if (status === 422) {
          const left = remainingAttempts(err) ?? null;
          setRemaining(left);
          setSubmitError(
            left === null
              ? 'Código incorreto.'
              : `Código incorreto. Tentativas restantes: ${left}.`,
          );
        } else if (status === 423) {
          setSubmitError(
            'Convite bloqueado por tentativas excessivas. Solicite reenvio ao admin.',
          );
        } else if (status === 410) {
          setSubmitError('Convite expirado. Solicite reenvio ao admin.');
        } else if (status === 404) {
          setSubmitError('Convite inválido. Solicite reenvio ao admin.');
        } else {
          setSubmitError(describeError(err));
        }
      } else {
        setSubmitError(describeError(err));
      }
    } finally {
      setSubmitting(false);
    }
  }

  // Error states pre-lookup ---------------------------------------------------
  if (lookupError) {
    return (
      <div style={pageStyle}>
        <div style={cardStyle}>
          <h1 style={{ marginTop: 0 }}>Convite indisponível</h1>
          <p style={{ color: 'var(--color-text-secondary)' }}>
            {lookupStatus === 410
              ? 'Convite expirado. Solicite reenvio ao admin.'
              : 'Não foi possível abrir este convite. Solicite reenvio ao admin.'}
          </p>
        </div>
      </div>
    );
  }
  if (!lookup) {
    return (
      <div style={pageStyle}>
        <div style={cardStyle}>Carregando…</div>
      </div>
    );
  }

  return (
    <div style={pageStyle}>
      <div style={cardStyle}>
        <h1 style={{ marginTop: 0 }}>Defina sua senha</h1>
        <p style={{ color: 'var(--color-text-secondary)' }}>
          Olá <strong>{lookup.fullName}</strong>, você foi convidada(o) para o{' '}
          <strong>Vapt Platform</strong>. Este link expira em{' '}
          {expiresHumanShort(lookup.expiresAt)}.
        </p>

        <form onSubmit={handleSubmit}>
          <fieldset style={{ border: 'none', padding: 0, margin: 0 }}>
            <legend
              id="code-group-label"
              style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}
            >
              Código de verificação de 4 caracteres
            </legend>
            <div className="btn-row">
              {codeChars.map((char, idx) => (
                <input
                  key={idx}
                  ref={(el) => {
                    inputsRef.current[idx] = el;
                  }}
                  type="text"
                  inputMode="text"
                  autoCapitalize="characters"
                  autoComplete="off"
                  aria-label={`caractere ${idx + 1} de ${CODE_LEN}`}
                  value={char}
                  onChange={(e) => handleCodeChange(idx, e.target.value)}
                  onKeyDown={(e) => handleCodeKeyDown(idx, e)}
                  maxLength={CODE_LEN /* allow paste */}
                  style={{
                    width: 56,
                    height: 56,
                    fontSize: 28,
                    fontFamily: 'ui-monospace, monospace',
                    textAlign: 'center',
                    border: '2px solid var(--color-border)',
                    borderRadius: 8,
                    textTransform: 'uppercase',
                  }}
                />
              ))}
            </div>
          </fieldset>

          <div className="field" style={{ marginTop: 24 }}>
            <label htmlFor="activate-password">Nova senha</label>
            <input
              id="activate-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
              aria-describedby="password-hints"
            />
            <ul
              id="password-hints"
              style={{ listStyle: 'none', padding: 0, margin: '8px 0', fontSize: 12 }}
            >
              <Hint ok={criteria.length}>Pelo menos 12 caracteres</Hint>
              <Hint ok={criteria.upper}>Pelo menos 1 letra MAIÚSCULA</Hint>
              <Hint ok={criteria.lower}>Pelo menos 1 letra minúscula</Hint>
              <Hint ok={criteria.digit}>Pelo menos 1 dígito</Hint>
              <Hint ok={criteria.symbol}>Pelo menos 1 símbolo</Hint>
            </ul>
          </div>

          {submitError && (
            <div className="error-banner" role="alert">
              {submitError}
              {remaining !== null && remaining <= 1 && (
                <div style={{ fontSize: 12, marginTop: 4 }}>
                  Após 0 tentativas o convite será bloqueado.
                </div>
              )}
            </div>
          )}

          <button
            type="submit"
            className="btn"
            disabled={!passwordOk || !codeOk || submitting}
            style={{
              width: '100%',
              marginTop: 16,
              padding: '12px 16px',
              borderRadius: 8,
            }}
          >
            {submitting ? 'Ativando…' : 'Definir senha e continuar para MFA'}
          </button>
        </form>
      </div>
    </div>
  );
}

function Hint({ ok, children }: { ok: boolean; children: React.ReactNode }) {
  return (
    <li
      data-met={ok ? 'true' : 'false'}
      style={{
        color: ok ? 'var(--color-success)' : 'var(--color-text-tertiary)',
        display: 'flex',
        alignItems: 'center',
        gap: 6,
      }}
    >
      {/* Ícone Lucide de traço no lugar do ✓ e do ○ (regra 10, A15). */}
      {ok ? <Check size={16} aria-hidden="true" /> : <Minus size={16} aria-hidden="true" />}
      <span>{children}</span>
    </li>
  );
}

function expiresHumanShort(iso: string): string {
  const diffMs = new Date(iso).getTime() - Date.now();
  if (diffMs <= 0) return 'menos de 1 hora';
  const hours = Math.round(diffMs / 3_600_000);
  if (hours < 24) return `${hours} h`;
  const days = Math.round(hours / 24);
  return `${days} dia${days === 1 ? '' : 's'}`;
}

const pageStyle: React.CSSProperties = {
  minHeight: '100vh',
  background: 'var(--color-background)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 24,
};
const cardStyle: React.CSSProperties = {
  background: 'var(--color-surface)',
  borderRadius: 12,
  padding: 32,
  maxWidth: 460,
  width: '100%',
  boxShadow: 'var(--shadow-lg)',
};
